import React, { useCallback, useEffect, useState } from "react";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ProgressBar from "../../../../UI/ProgressBar";
import {
  EMPLOYEE_SEQUENTIAL_PROGRESS_PAGE_SIZE,
  useGetSuperAdminEmployeeSequentialProgressQuery,
} from "../../../../../redux-toolkit/features/reports/ReportsApiSlice";
import { useInfiniteScroll } from "../../../../../hooks/useInfiniteScroll";
import { Error404 } from "../../../../UI/404/Error404";
import { SortIcon } from "../../../Manage Organization/Employee/EmployeeTable";
import emptyStateSearch from "../../../../../assets/employee/empty_state_search.svg";
import { ReportsTruncatedName } from "../../ReportsTruncatedName";
import { getFreezeVars, getFrozenColClass } from "../../reportsFrozenCols";

const formatPercent = (value) =>
  value == null ? "0%" : `${Number(value).toFixed(1)}%`;

const formatWaitingSince = (value) => {
  if (!value) return "--";
  try {
    return new Date(value).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "--";
  }
};

const statusChip = (status) => {
  if (status === "Waiting Approval") {
    return (
      <Chip
        label="Waiting Approval"
        size="small"
        sx={{
          fontWeight: 700,
          bgcolor: "rgba(255, 190, 24, 0.25)",
          color: "#9a6700",
        }}
      />
    );
  }

  if (status === "Not Started") {
    return (
      <Chip
        label="Not Started"
        size="small"
        sx={{
          fontWeight: 700,
          bgcolor: "rgba(211, 47, 47, 0.12)",
          color: "#c62828",
        }}
      />
    );
  }

  return (
    <Chip
      label="Active"
      size="small"
      sx={{
        fontWeight: 700,
        bgcolor: "rgba(25, 118, 210, 0.12)",
        color: "#1565c0",
      }}
    />
  );
};

const SEQ_FROZEN_KEYS = ["employee", "company"];
const SEQ_FROZEN_WIDTHS = {
  employee: 200,
  company: 200,
};

export const EmployeeSequentialProgressTable = ({
  check,
  searchQuery = "",
  getTableData,
}) => {
  const [sortBy, setSortBy] = useState("");
  const [sortType, setSortType] = useState("asc");
  const [page, setPage] = useState(1);
  const [scrollContainer, setScrollContainer] = useState(null);

  const { data, isError, isLoading, isFetching } =
    useGetSuperAdminEmployeeSequentialProgressQuery(
      {
        sortBy,
        type: sortType,
        query: searchQuery,
        page,
        limit: EMPLOYEE_SEQUENTIAL_PROGRESS_PAGE_SIZE,
      },
      {
        refetchOnMountOrArgChange: true,
      }
    );

  const pagination = data?.pagination;
  const hasMore = pagination ? page < pagination.totalPages : false;
  const rows = data?.data ?? [];

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setPage((currentPage) => currentPage + 1);
    }
  }, [isFetching, hasMore]);

  const sentinelRef = useInfiniteScroll({
    hasMore,
    isLoading: isFetching,
    onLoadMore: loadMore,
    scrollRootElement: scrollContainer,
  });

  useEffect(() => {
    setPage(1);
  }, [sortBy, sortType, searchQuery]);

  useEffect(() => {
    if (!data) return;
    getTableData && getTableData(data);
  }, [data]);

  const handleSorting = (sortKey) => {
    setSortBy(sortKey);
    setSortType(!sortType || sortType === "desc" ? "asc" : "desc");
  };

  if (isLoading && page === 1) {
    return <ProgressBar />;
  }

  if (isError) {
    return <Error404 />;
  }

  const allFieldsDeselected = Object.values(check).every((v) => !v);
  const visibleColumnCount = Object.values(check).filter(Boolean).length || 1;
  const frozenKeys = SEQ_FROZEN_KEYS.filter((key) => check[key]);
  const freezeVars = getFreezeVars(frozenKeys, SEQ_FROZEN_WIDTHS);

  return (
    <div
      style={
        allFieldsDeselected
          ? { overflow: "hidden", maxWidth: "100%", width: "100%" }
          : undefined
      }
    >
      <TableContainer
        ref={setScrollContainer}
        sx={{
          height: allFieldsDeselected ? "auto" : "100%",
          maxHeight: allFieldsDeselected ? "none" : "calc(100vh - 270px)",
          padding: "0 1px",
          width: "100%",
          maxWidth: "100%",
          overflowX: allFieldsDeselected ? "hidden" : "auto",
          overflowY: allFieldsDeselected ? "visible" : "auto",
        }}
      >
        <Table
          stickyHeader
          aria-label="employee sequential progress table"
          className="reports-table--freeze"
          sx={
            allFieldsDeselected
              ? freezeVars
              : { minWidth: 1600, ...freezeVars }
          }
        >
          <TableHead>
            <TableRow className="tableUi__head">
              {allFieldsDeselected ? (
                <TableCell align="center">Select fields to view data</TableCell>
              ) : (
                <>
                  {check.employee && (
                    <TableCell
                      className={getFrozenColClass("employee", frozenKeys)}
                      align="center"
                      onClick={() => handleSorting("employee_name")}
                    >
                      <div className="tableUi__head_sortable">
                        Employee <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.company && (
                    <TableCell
                      className={getFrozenColClass("company", frozenKeys)}
                      align="center"
                      onClick={() => handleSorting("company_name")}
                    >
                      <div className="tableUi__head_sortable">
                        Company <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.currentBookName && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("currentBookName")}
                    >
                      <div className="tableUi__head_sortable">
                        Current Book Name <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.booksCompleted && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("booksCompleted")}
                    >
                      <div className="tableUi__head_sortable">
                        Books Completed <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.booksRemaining && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("booksRemaining")}
                    >
                      <div className="tableUi__head_sortable">
                        Books Remaining <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.currentBookOrder && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("currentBookOrder")}
                    >
                      <div className="tableUi__head_sortable">
                        Current Book # <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.tasksDoneAll && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("tasksDoneAll")}
                    >
                      <div className="tableUi__head_sortable">
                        Tasks Done (All Books){" "}
                        <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.totalTasksAll && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("totalTasksAll")}
                    >
                      <div className="tableUi__head_sortable">
                        Total Tasks (All Books){" "}
                        <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.overallProgressPercent && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("overallProgressPercent")}
                    >
                      <div className="tableUi__head_sortable">
                        Overall Progress % <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.tasksDoneCurrent && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("tasksDoneCurrent")}
                    >
                      <div className="tableUi__head_sortable">
                        Tasks Done (Current Book){" "}
                        <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.totalTasksCurrent && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("totalTasksCurrent")}
                    >
                      <div className="tableUi__head_sortable">
                        Total Tasks (Current Book){" "}
                        <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.currentProgressPercent && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("currentProgressPercent")}
                    >
                      <div className="tableUi__head_sortable">
                        Current Progress % <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.status && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("status")}
                    >
                      <div className="tableUi__head_sortable">
                        Status <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.waitingSince && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("waitingSince")}
                    >
                      <div className="tableUi__head_sortable">
                        Waiting Since <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {allFieldsDeselected ? (
              <TableRow>
                <TableCell align="center" sx={{ border: "none", py: 6 }}>
                  Select fields to view data
                </TableCell>
              </TableRow>
            ) : searchQuery && rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumnCount}
                  align="center"
                  sx={{ border: "none", py: 8 }}
                >
                  <img
                    src={emptyStateSearch}
                    alt="No search results"
                    style={{ width: "220px", height: "165px" }}
                  />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumnCount}
                  align="center"
                  sx={{ border: "none", py: 8 }}
                >
                  No employees found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <React.Fragment key={row._id}>
                  <TableRow className="tableUi___body">
                    {check.employee && (
                      <TableCell
                        className={getFrozenColClass("employee", frozenKeys)}
                        align="center"
                      >
                        {row.employeeName}
                      </TableCell>
                    )}
                    {check.company && (
                      <TableCell
                        className={getFrozenColClass("company", frozenKeys)}
                        align="center"
                      >
                        <ReportsTruncatedName value={row.company?.name} empty="" />
                      </TableCell>
                    )}
                    {check.currentBookName && (
                      <TableCell align="center">
                        {row.currentBookName || "--"}
                      </TableCell>
                    )}
                    {check.booksCompleted && (
                      <TableCell align="center">{row.booksCompleted}</TableCell>
                    )}
                    {check.booksRemaining && (
                      <TableCell align="center">{row.booksRemaining}</TableCell>
                    )}
                    {check.currentBookOrder && (
                      <TableCell align="center">
                        {row.currentBookOrder}
                      </TableCell>
                    )}
                    {check.tasksDoneAll && (
                      <TableCell align="center">{row.tasksDoneAll}</TableCell>
                    )}
                    {check.totalTasksAll && (
                      <TableCell align="center">{row.totalTasksAll}</TableCell>
                    )}
                    {check.overallProgressPercent && (
                      <TableCell align="center">
                        {formatPercent(row.overallProgressPercent)}
                      </TableCell>
                    )}
                    {check.tasksDoneCurrent && (
                      <TableCell align="center">
                        {row.tasksDoneCurrent}
                      </TableCell>
                    )}
                    {check.totalTasksCurrent && (
                      <TableCell align="center">
                        {row.totalTasksCurrent}
                      </TableCell>
                    )}
                    {check.currentProgressPercent && (
                      <TableCell align="center">
                        {formatPercent(row.currentProgressPercent)}
                      </TableCell>
                    )}
                    {check.status && (
                      <TableCell align="center">
                        {statusChip(row.status)}
                      </TableCell>
                    )}
                    {check.waitingSince && (
                      <TableCell align="center">
                        {formatWaitingSince(row.waitingSince)}
                      </TableCell>
                    )}
                  </TableRow>
                </React.Fragment>
              ))
            )}
            {!allFieldsDeselected && (
              <TableRow ref={sentinelRef}>
                <TableCell
                  colSpan={visibleColumnCount}
                  align="center"
                  sx={{ border: "none", py: 1 }}
                >
                  {isFetching && page > 1 && <ProgressBar />}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
